import {
  Button,
  Chip,
  Box,
  Typography,
  TextField,
  InputAdornment,
  Grid,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import coursesService from "../../services/CoursesService";
import teacherService from "../../services/TeacherService";
import { useUsuarioConCedula } from "../../hooks/useUsuarioConCedula";
import { useWelcomeCourseStatus } from "../../hooks/useWelcomeCourseStatus";
import { isCourseEnrolledThisWeek } from "../../utils/recentEnrollments";
import { CourseCard } from "./Cview";
import WelcomeCourseBanner from "../common/WelcomeCourseBanner";

const getCourseTeacherId = (course) => {
  if (!course) return null;
  const id =
    course.teacherId ??
    course.teacher_id ??
    course.TeacherId ??
    course.Teacher_Id ??
    course.teached_id ??
    course.TeachedId;
  if (id === undefined || id === null || id === "") return null;
  const num = Number(id);
  return Number.isNaN(num) ? id : num;
};

// Mismos breakpoints que Cview en modo compact (Home → Cursos)
const compactGridItemSx = {
  width: "280px !important",
  maxWidth: "280px !important",
  flexBasis: "280px !important",
  "@media (max-width: 1280px)": {
    width: "280px !important",
    maxWidth: "280px !important",
    flexBasis: "280px !important",
  },
  "@media (max-width: 960px)": {
    width: "calc(50% - 10px) !important",
    maxWidth: "calc(50% - 10px) !important",
    flexBasis: "calc(50% - 10px) !important",
  },
  "@media (max-width: 600px)": {
    width: "100% !important",
    maxWidth: "100% !important",
    flexBasis: "100% !important",
  },
};

const stripHtmlTags = (html) => {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
};

function MyCourses() {
  const [myCourses, setMyCourses] = useState([]);
  const [progressMap, setProgressMap] = useState({});
  const [teacherNames, setTeacherNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedDescriptions, setExpandedDescriptions] = useState({});

  const navigate = useNavigate();
  const { usuarioConCedula, isLoadingUsuario } = useUsuarioConCedula();
  const {
    isBlocked: welcomeBlocked,
    courseId: welcomeCourseId,
    courseTitle: welcomeCourseTitle,
    isWelcomeCourse,
  } = useWelcomeCourseStatus();

  const goToCourse = useCallback(
    (course) => {
      if (welcomeBlocked && !isWelcomeCourse(course?.id)) {
        if (welcomeCourseId) {
          navigate(`/courses/Introcourse/${welcomeCourseId}`, {
            state: { objeto: welcomeCourseId },
          });
        }
        return;
      }
      navigate(`/courses/Introcourse/${course.id}`, { state: { objeto: course.id } });
    },
    [navigate, welcomeBlocked, isWelcomeCourse, welcomeCourseId]
  );

  const fetchCourses = useCallback(async () => {
    const userId = usuarioConCedula?.id ?? usuarioConCedula?.Id;
    if (!userId) {
      setMyCourses([]);
      setProgressMap({});
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const rawCourses = await coursesService.getUserCourses(userId, navigate);
      const courses = coursesService.normalizeCoursesList(rawCourses || []);
      setMyCourses(courses);
      // Mostrar cursos de inmediato; progreso/teachers no deben bloquear la UI
      setLoading(false);

      const teacherIds = [
        ...new Set(
          courses
            .map((c) => getCourseTeacherId(c))
            .filter((id) => id != null && Number(id) > 0)
        ),
      ];
      const namesMap = {};
      await Promise.all(
        teacherIds.map(async (id) => {
          try {
            const r = await teacherService.getTeacher(id);
            namesMap[id] = r.name || r.nombre || r.Name || null;
          } catch (e) {
            console.warn(`No se pudo cargar instructor ${id}:`, e);
          }
        })
      );
      setTeacherNames(namesMap);

      const progressData = {};
      await Promise.all(
        courses.map(async (course) => {
          const courseId = course.id;
          if (courseId == null || courseId === '') {
            return;
          }
          try {
            const result = await coursesService.getCourseProgress(userId, courseId, navigate);
            const raw = Number(result?.data?.progressPercentage);
            progressData[courseId] = Number.isFinite(raw) ? Math.floor(raw) : 0;
          } catch (error) {
            console.error("Error getting progress for course", courseId, ":", error);
            progressData[courseId] = 0;
          }
        })
      );

      setProgressMap(progressData);
    } catch (error) {
      console.error("Error al obtener los cursos:", error);
      setMyCourses([]);
      setProgressMap({});
      setLoading(false);
    }
  }, [usuarioConCedula?.id, navigate]);

  useEffect(() => {
    if (isLoadingUsuario) return;
    fetchCourses();
  }, [fetchCourses, isLoadingUsuario]);

  const getCourseProgressValue = (courseId) => {
    const value = progressMap[courseId];
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
  };

  const filteredCourses = myCourses.filter((course) => {
    if (!course?.id) return false;
    if (welcomeBlocked && welcomeCourseId && String(course.id) !== String(welcomeCourseId)) {
      return false;
    }
    const q = searchTerm.toLowerCase();
    return (
      stripHtmlTags(course.title || '').toLowerCase().includes(q) ||
      stripHtmlTags(course.description || '').toLowerCase().includes(q)
    );
  });

  const completedCourses = filteredCourses.filter((course) => getCourseProgressValue(course.id) === 100);
  const inProgressCourses = filteredCourses.filter((course) => {
    const p = getCourseProgressValue(course.id);
    return p > 0 && p < 100;
  });
  const notStartedCourses = filteredCourses.filter((course) => getCourseProgressValue(course.id) === 0);
  const recentCourses = filteredCourses.filter((course) => isCourseEnrolledThisWeek(course));
  const recentCourseIds = new Set(recentCourses.map((course) => course.id));

  const completedCoursesExcludingRecent = completedCourses.filter((course) => !recentCourseIds.has(course.id));
  const inProgressCoursesExcludingRecent = inProgressCourses.filter((course) => !recentCourseIds.has(course.id));
  const notStartedCoursesExcludingRecent = notStartedCourses.filter((course) => !recentCourseIds.has(course.id));

  const toggleDescription = (courseId) => {
    setExpandedDescriptions((prev) => ({
      ...prev,
      [courseId]: !prev[courseId],
    }));
  };

  const renderCourseGrid = (courses) => (
    <Grid container spacing={2.5} justifyContent="flex-start" alignItems="stretch" sx={{ mb: 0 }}>
      {courses.map((course) => (
        <Grid item key={course.id} xs={12} sm={6} md={3} lg={3} sx={{ ...compactGridItemSx, display: 'flex' }}>
          <CourseCard
            course={course}
            teacherNames={teacherNames}
            onOpenModal={() => {}}
            onToggleDescription={toggleDescription}
            isExpanded={!!expandedDescriptions[course.id]}
            onEnroll={async () => {}}
            isEnrolled
            hasPending={false}
            navigate={navigate}
            compact
            onCompactNavigate={goToCourse}
            progressPercent={getCourseProgressValue(course.id)}
            showMyCoursesButton
          />
        </Grid>
      ))}
    </Grid>
  );

  const hasVisibleSections =
    recentCourses.length > 0 ||
    inProgressCoursesExcludingRecent.length > 0 ||
    notStartedCoursesExcludingRecent.length > 0 ||
    completedCoursesExcludingRecent.length > 0;

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center space-y-4 h-64">
        <CircularProgress size={50} sx={{ color: "#4DABA4" }} />
        <Typography variant="body1" color="textSecondary">
          Cargando tus cursos...
        </Typography>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 mx-auto max-w-7xl">
      {welcomeBlocked && (
        <WelcomeCourseBanner
          courseId={welcomeCourseId}
          courseTitle={welcomeCourseTitle}
        />
      )}
      <div className="mb-8">
        <Typography variant="h4" className="mb-2 font-bold text-gray-900">
          Mis Cursos
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Continúa aprendiendo y alcanza tus objetivos
        </Typography>
      </div>

      <div className="mb-8">
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Buscar cursos por título o descripción..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#4DABA4" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            maxWidth: 600,
            "& .MuiOutlinedInput-root": {
              borderRadius: "16px",
              backgroundColor: "#F9FAFB",
              "&:hover": {
                backgroundColor: "#F3F4F6",
              },
              "&.Mui-focused": {
                backgroundColor: "white",
              },
            },
          }}
        />
      </div>

      {myCourses.length === 0 ? (
        <div className="flex flex-col justify-center items-center p-12 bg-gradient-to-r from-blue-50 to-green-50 rounded-2xl shadow-lg">
          <div className="mb-4 text-6xl">📚</div>
          <Typography variant="h5" className="mb-2 font-bold text-gray-800">
            ¡Comienza tu viaje de aprendizaje!
          </Typography>
          <Typography variant="body1" color="textSecondary" className="mb-6 max-w-md text-center">
            No tienes cursos inscritos aún. Explora nuestro catálogo y encuentra el curso perfecto para ti.
          </Typography>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#4DABA4",
              borderRadius: "12px",
              textTransform: "none",
              px: 4,
              py: 1.5,
              fontWeight: "bold",
            }}
          >
            Explorar Cursos
          </Button>
        </div>
      ) : (
        <div className="space-y-10">
          {recentCourses.length > 0 && (
            <section>
              <div className="flex items-center mb-6">
                <div className="mr-3 w-1 h-8 bg-[#4DABA4] rounded-full" />
                <Typography variant="h5" className="font-bold text-gray-800">
                  Nuevos esta semana
                </Typography>
                <Chip
                  label={recentCourses.length}
                  size="small"
                  sx={{ ml: 2, backgroundColor: "#4DABA4", color: "white", fontWeight: 700 }}
                />
              </div>
              {renderCourseGrid(recentCourses)}
            </section>
          )}

          {inProgressCoursesExcludingRecent.length > 0 && (
            <section>
              <div className="flex items-center mb-6">
                <div className="mr-3 w-1 h-8 bg-blue-500 rounded-full" />
                <Typography variant="h5" className="font-bold text-gray-800">
                  En Progreso
                </Typography>
                <Chip
                  label={inProgressCoursesExcludingRecent.length}
                  size="small"
                  sx={{ ml: 2, backgroundColor: "#3B82F6", color: "white" }}
                />
              </div>
              {renderCourseGrid(inProgressCoursesExcludingRecent)}
            </section>
          )}

          {notStartedCoursesExcludingRecent.length > 0 && (
            <section>
              <div className="flex items-center mb-6">
                <div className="mr-3 w-1 h-8 bg-gray-500 rounded-full" />
                <Typography variant="h5" className="font-bold text-gray-800">
                  Por Comenzar
                </Typography>
                <Chip
                  label={notStartedCoursesExcludingRecent.length}
                  size="small"
                  sx={{ ml: 2, backgroundColor: "#6B7280", color: "white" }}
                />
              </div>
              {renderCourseGrid(notStartedCoursesExcludingRecent)}
            </section>
          )}

          {completedCoursesExcludingRecent.length > 0 && (
            <section>
              <div className="flex items-center mb-6">
                <div className="mr-3 w-1 h-8 bg-green-500 rounded-full" />
                <Typography variant="h5" className="font-bold text-gray-800">
                  Completados
                </Typography>
                <Chip
                  label={completedCoursesExcludingRecent.length}
                  size="small"
                  sx={{ ml: 2, backgroundColor: "#10B981", color: "white" }}
                />
              </div>
              {renderCourseGrid(completedCoursesExcludingRecent)}
            </section>
          )}

          {!hasVisibleSections && filteredCourses.length > 0 && (
            <section>
              <div className="flex items-center mb-6">
                <div className="mr-3 w-1 h-8 bg-[#4DABA4] rounded-full" />
                <Typography variant="h5" className="font-bold text-gray-800">
                  Tus cursos
                </Typography>
                <Chip
                  label={filteredCourses.length}
                  size="small"
                  sx={{ ml: 2, backgroundColor: "#4DABA4", color: "white", fontWeight: 700 }}
                />
              </div>
              {renderCourseGrid(filteredCourses)}
            </section>
          )}

          {filteredCourses.length === 0 && searchTerm && (
            <Box className="flex flex-col justify-center items-center p-12 bg-gray-50 rounded-2xl">
              <div className="mb-4 text-4xl">🔍</div>
              <Typography variant="h6" className="mb-2 font-bold text-gray-800">
                No se encontraron resultados
              </Typography>
              <Typography variant="body1" color="textSecondary" className="text-center">
                No hay cursos que coincidan con &quot;{searchTerm}&quot;
              </Typography>
            </Box>
          )}
        </div>
      )}
    </div>
  );
}

export default MyCourses;
